const API_BASE = "/api";

export interface RaceCosmetics {
  char: string | null;
  hat: string | null;
  trail: string | null;
}

export interface RaceSlot {
  y: number | null;
  score: number;
  alive: boolean;
  done: boolean;
  cosmetics?: RaceCosmetics | null;
}

export interface RaceState {
  id: string;
  seed: number;
  status: "waiting" | "racing" | "done";
  startAt: number | null;
  you: "p1" | "p2";
  opponent: string | null;
  self: RaceSlot;
  other: RaceSlot;
  winner: string | null;
}

/** PUBLIC snapshot of who's racing right now (no auth required). */
export interface RaceOnline {
  count: number;
  players: string[];
}

/** Everything PlayScene needs to run a synchronized ghost race. */
export interface RaceRun {
  id: string;
  seed: number;
  startAt: number;
  opponent: string;
  /** Set when this race settles a flies battle (so GameOverScene shows the payout). */
  battleId?: string;
  stake?: number;
}

/**
 * Poll-based live "ghost race": two players are matched onto a shared seed and a
 * synchronized start time, then exchange positions a few times a second so each
 * renders the other as a translucent ghost. No WebSockets — just short polls.
 */
export class Races {
  async quick(name: string, token: string): Promise<RaceState | null> {
    return this.req("POST", "/races/quick", token, { name });
  }

  /** PUBLIC: how many players are in races now (visible logged-out too). */
  async online(): Promise<RaceOnline | null> {
    try {
      const res = await fetch(`${API_BASE}/races/online`);
      if (!res.ok) return null;
      const data = (await res.json()) as RaceOnline;
      return {
        count: Math.max(0, Number(data.count) || 0),
        players: Array.isArray(data.players) ? data.players : [],
      };
    } catch {
      return null;
    }
  }

  /** Start (or join) a live race against a confirmed friend by name. */
  async friend(name: string, token: string, opponent: string): Promise<RaceState | null> {
    return this.req("POST", "/races/friend", token, { name, opponent });
  }

  async poll(name: string, token: string, id: string): Promise<RaceState | null> {
    return this.req(
      "GET",
      `/races/${encodeURIComponent(id)}?name=${encodeURIComponent(name)}`,
      token,
    );
  }

  /** Push my live state and receive the opponent's latest. */
  async pos(
    name: string,
    token: string,
    id: string,
    s: {
      y: number;
      score: number;
      alive: boolean;
      done: boolean;
      cosmetics?: RaceCosmetics;
    },
  ): Promise<RaceState | null> {
    return this.req("POST", `/races/${encodeURIComponent(id)}/pos`, token, { name, ...s });
  }

  async cancel(name: string, token: string, id: string): Promise<void> {
    await this.req("POST", `/races/${encodeURIComponent(id)}/cancel`, token, { name });
  }

  private async req(
    method: "GET" | "POST",
    path: string,
    token: string,
    body?: Record<string, unknown>,
  ): Promise<RaceState | null> {
    try {
      const res = await fetch(`${API_BASE}${path}`, {
        method,
        headers: {
          authorization: `Bearer ${token}`,
          ...(body ? { "content-type": "application/json" } : {}),
        },
        ...(body ? { body: JSON.stringify(body) } : {}),
      });
      if (!res.ok) return null;
      return (await res.json()) as RaceState;
    } catch {
      return null;
    }
  }
}
